/**
 * Trip CO₂ Calculator — public geocoding client.
 *
 * Wraps `GET /api/public/geocoding/cities/search` (no auth). The
 * response shape matches the auth-gated `features/geocoding/api.ts`
 * — the public route exists only because the calculator can't
 * require login.
 *
 * Phase 7 will add IP-quota middleware on the server side (the
 * Mapbox token is server-side, so unauth queries bill against the
 * same upstream key). This client does not need to change for that.
 */

import type { CityResult } from '../geocoding/api';
import type { CitySearchFn } from '../geocoding/CitySearchInput';
import { isTestBypassActive } from './testBypass';

// Re-export the type so callers don't need to reach into the geocoding feature
// for what is, semantically, the same shape.
export type { CityResult } from '../geocoding/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

/**
 * Free-text city search via the **public** route. Drop-in compatible
 * with the auth-gated `searchCities` from `features/geocoding/api.ts`
 * — same signature, same return shape — so it can be passed straight
 * into `<CitySearchInput searchFn={searchCitiesPublic} />`.
 */
export const searchCitiesPublic: CitySearchFn = async (
  query,
  opts
): Promise<CityResult[]> => {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const params = new URLSearchParams({ q: trimmed });
  if (opts?.limit) params.set('limit', String(opts.limit));
  // Forward `?test=1` from the page URL so the backend's geocoding
  // rate-limiter bypass applies during QA. See `./testBypass.ts`.
  if (isTestBypassActive()) params.set('test', '1');

  const response = await fetch(
    `${API_BASE}/public/geocoding/cities/search?${params.toString()}`,
    { method: 'GET', signal: opts?.signal }
  );

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const payload = await response.json();
      if (payload?.error) message = payload.error;
    } catch {
      // Fall through.
    }
    throw new Error(message);
  }

  const data = (await response.json()) as { results?: CityResult[] };
  return data.results ?? [];
};
